import React from 'react';
import { Link } from 'react-router-dom';
import { Compass, ArrowRight } from 'lucide-react';

/**
 * AdvisorEntryBanner — CTA compacto que aparece en Search y Property Detail
 * cuando el usuario aún NO ha llegado a "Mi decisión".
 *
 * Su único trabajo: comunicar el beneficio ("Encuentra la mejor decisión")
 * y llevar al Workspace.
 *
 * Props:
 *   - variant: 'search' | 'detail'
 *   - dismissible: default true
 *   - onDismiss: opcional
 */
export default function AdvisorEntryBanner({
  variant = 'search',
  className = '',
  dismissible = true,
  onDismiss,
}) {
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    // Respetar dismiss por 24h por variante
    const key = `advisor_entry_dismissed_${variant}`;
    const at = sessionStorage.getItem(key);
    if (at && Date.now() - Number(at) < 24 * 3600 * 1000) {
      setDismissed(true);
    }
  }, [variant]);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem(
      `advisor_entry_dismissed_${variant}`,
      String(Date.now()),
    );
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  const copy = {
    search: {
      title: 'Encuentra la mejor decisión para ti',
      sub: 'Con Habita Advisor organizamos todo tu proceso — recomendaciones, comparaciones y decisión en un solo lugar.',
    },
    detail: {
      title: '¿Te sirve para tu decisión?',
      sub: 'Guarda esta propiedad en tu proceso y compárala con otras cuando estés listo.',
    },
  }[variant];

  return (
    <div
      data-testid={`advisor-entry-${variant}`}
      className={`relative overflow-hidden rounded-2xl border border-habita/20 bg-gradient-to-r from-habita-mint/60 via-habita-mint/30 to-white p-4 sm:p-5 ${className}`}
    >
      {dismissible && (
        <button
          onClick={handleDismiss}
          aria-label="Cerrar"
          data-testid={`advisor-entry-${variant}-dismiss`}
          className="absolute top-2 right-3 text-gray-400 hover:text-habita-dark text-lg leading-none"
        >
          ×
        </button>
      )}
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-habita text-white grid place-items-center shrink-0">
          <Compass className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg sm:text-xl font-bold text-habita-dark leading-snug pr-6">
            {copy.title}
          </p>
          <p className="text-sm text-gray-700 mt-1 max-w-2xl">
            {copy.sub}
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Link
              to="/advisor/inicio"
              data-testid={`advisor-entry-${variant}-cta`}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-habita text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Comenzar con Habita Advisor
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <span className="text-[11px] text-gray-500">Sin registro para empezar</span>
          </div>
        </div>
      </div>
    </div>
  );
}
